import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { Box, Flex, useToken } from "@chakra-ui/react";
import { PanInfo, Reorder } from "framer-motion";
import { useRef } from "react";
import { useIntl } from "react-intl";

import { ContextMenu } from "@nens/chakra/components";
import { RanaContextMenu, RanaEditable, RanaEditableHandle } from "@nens/ranalib";

import { RanaFileIcon } from "@/lib/rana-file-data-type";

import { LayerFromFileTooltip } from "../../../components/from-file-tooltip";
import { getFileByPath } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { Folder, Layer } from "../../../types/publication";
import { useLayerListItemActions } from "../hooks/use-file-layer-item-actions";

export function LayerItem({
  layer,
  folder,
  indent,
  isFirst,
  isLast,
  onDragStart,
  removeLayer,
  onLayerClick,
  onDrag,
  onDragEnd,
}: {
  layer: Layer;
  folder?: Folder;
  indent: number;
  isFirst: boolean;
  isLast: boolean;
  onDragStart: () => void;
  removeLayer: (layer: Layer) => void;
  onLayerClick: (layer: Layer, e: React.MouseEvent) => void;
  onDrag?: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void;
  onDragEnd: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void;
}) {
  const intl = useIntl();

  const toggleLayerVisibility = usePublicationStore((state) => state.toggleLayerVisibility);
  const selectLayer = usePublicationStore((state) => state.selectLayer);
  const changeLayerName = usePublicationStore((state) => state.changeLayerName);
  const isSelected = layer.selected;
  const file = usePublicationStore(getFileByPath(layer.filePath));

  const editRef = useRef<RanaEditableHandle>(null);
  const actions = useLayerListItemActions(
    layer,
    () => editRef.current?.startEditing(),
    removeLayer,
    isFirst,
    isLast,
    folder
  );

  const [indentPixel, oneToPixel] = useToken("sizes", [indent, 1]);
  const onLayerVisibilityClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleLayerVisibility(layer);
  };

  // Nice to display the layer from the file for vectors as they can have multiple layers.
  // For rasters and scenarios the layer name is way too technical and not actually displayed on the detail page so skip it.
  const layerInFileLabel =
    layer.layerInFile && file?.file.dataType === "vector" ? `"${layer.layerInFile}" ` : "";

  const label = intl.formatMessage(
    {
      id: "publication.layer.title.layer_from_file",
      defaultMessage: 'Layer {layerInFileLabel}from file: "{filename}"',
    },
    { filename: layer.filePath, layerInFileLabel }
  );

  return (
    <LayerFromFileTooltip label={label}>
      <Reorder.Item
        as="div"
        value={layer.id}
        data-row-id={layer.id}
        style={{ position: "relative" }}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
      >
        <ContextMenu<HTMLDivElement>
          renderMenu={actions?.length ? () => <RanaContextMenu.Actions actions={actions} /> : null}
          onOpen={() => {
            if (!isSelected) selectLayer(layer);
          }}
        >
          {(ref) => (
            <Flex
              flexDirection="row"
              alignItems="center"
              gap={2}
              ml={`calc(${indentPixel} * 4 + ${oneToPixel})`}
              borderLeftColor="brand.glacier"
              borderLeftWidth={indent ? "thick" : undefined}
              p={2}
              pl={4}
              cursor="pointer"
              role="group"
              bg={isSelected ? "brand.selected" : "transparent"}
              _hover={isSelected ? undefined : { bg: "button.tertiary.hover" }}
              onClick={(e) => onLayerClick(layer, e)}
              onContextMenu={(e) => {
                e.preventDefault();
                onLayerClick(layer, e as React.MouseEvent);
              }}
              ref={ref}
            >
              {
                file && (
                  <RanaFileIcon fileOrDirectory={file.file} />
                ) /* FUTURE: Ignores layer in file for now */
              }
              <Box flexGrow={1} overflow="hidden">
                <RanaEditable
                  key={layer.name}
                  defaultValue={layer.name}
                  onSubmit={(name) => changeLayerName(layer, name)}
                >
                  <RanaEditable.Preview
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  />
                  <RanaEditable.Input />
                  <RanaEditable.EditHandle ref={editRef} />
                </RanaEditable>
              </Box>
              {layer.visible ? (
                <ViewIcon
                  color="brand.sky"
                  boxSize={4}
                  onClick={onLayerVisibilityClick}
                  opacity={0.25}
                  _groupHover={{ opacity: 1 }}
                />
              ) : (
                <ViewOffIcon color="brand.sky" boxSize={4} onClick={onLayerVisibilityClick} />
              )}
              {actions.length > 0 && (
                <Box pr={1} visibility="hidden" _groupHover={{ visibility: "visible" }}>
                  <RanaContextMenu actions={actions} />
                </Box>
              )}
            </Flex>
          )}
        </ContextMenu>
      </Reorder.Item>
    </LayerFromFileTooltip>
  );
}
